/**
 * The authoring shape a model fills with literal words, and the one function
 * that turns it into a `ComposedSetup`. Phase 2b of
 * docs/plans/ai-setup-and-agent.md.
 *
 * A `ComposedSetup` is the right thing to store and the wrong thing to ask a
 * model for: every string in it is a key into a word table, every field
 * carries a storage slot, a component and an ord, and a formula names fields
 * the way a row does. A model asked for that gets one of the forty details
 * wrong on every call. So the model fills a `SetupDraft` instead, which
 * speaks in the person's words and nothing else, and `authorSetup` does the
 * bookkeeping: it mints the word keys, picks the component each type always
 * renders with, hands out slots per family in order, binds the purposes from
 * the direction the person described, writes the two views every record
 * ships with, and rewrites the formula's field keys into the shape the
 * template validator and the apply already read.
 *
 * Then `authorProblems` runs the authored setup through the same two checks
 * the compose route runs (`readComposedSetup`, `composeProblems`), plus the
 * few the authoring itself can find first, so the model is told every problem
 * at once and repairs in one round trip.
 *
 * Two things the draft deliberately does NOT hold.
 *
 *   A record keyed by field key. Gemini's function-calling schema drops
 *   `additionalProperties`, so a map would reach the model as an object with
 *   no properties at all. Every keyed thing here is a list of pairs instead.
 *
 *   The device's noun pair. `nounKey` and `entryNounKey` must be shipped
 *   keys until the device reads a composed one (compose.ts,
 *   NOUN_KEY_NOT_SHIPPED), so every composed record says `noun.record` and
 *   `noun.entry`, and the person's own word for one entry is kept in `words`
 *   under `c.<recordKey>.entry_noun`, where the phone can read it later.
 */
import {
  readComposedSetup,
  composeProblems,
  COMPOSED_TEMPLATE_ID,
  type ComposedLink,
  type ComposedMapping,
  type ComposedSetup,
} from './compose';
import {
  slotFamilyFor,
  type FieldSpec,
  type FormulaSpec,
  type FormulaAggOp,
  type FormulaCalcOp,
  type FormulaNode,
  type FormulaOnMissing,
  type FormulaResultType,
  type FormulaTestOp,
} from './formula';
// One reader for a flat formula, wherever it comes from: the setup tool's
// draft (here, naming fields by key) and every other tool (naming them by id).
import type { RecordCaptureTone, RecordIconId, VisibleRecordKind } from './api';
import { ENTRY_SLOT_COUNT, ENTRY_SLOT_SPILLS, type EntrySlotLetter } from './entry-slots';
import { resolveShape, type ShapeAsk } from './field-shapes';
import { LAYOUT_VERSION, type LayoutNode, type LayoutSectionStyle, type RecordLayout } from './layout';
import { readFormulaWire, type FormulaWire } from './formula-wire';
import type { ComponentId } from './generated/component-ids';
import { PRIMITIVE_FOR, VALUE_TYPES, type ValueTypeId } from './generated/value-types';
import type { InsightCardId } from './insights';
import type { LinkConditionOp, LinkPattern } from './links';
import type {
  TemplateBindingSpec,
  TemplateFieldSpec,
  TemplateRecordSpec,
  TemplateSpec,
  TemplateSummaryTile,
  TemplateTileCondition,
  TemplateViewSpec,
} from './templates/spec';
import type { TemplateProblem } from './templates/validate-template';

/* ------------------------------------------------------------- the shape */

/**
 * The value types a composed field may take: the sixteen the structure
 * editor offers (`ADDABLE_TYPES` in the server's structure-plan.ts). The
 * two left out need engines that do not exist yet, and offering one would
 * offer a field that shows nothing.
 *
 * `lines` is the one type that is more than a field: it carries the fields of
 * each line (`SetupDraftField.lines`), and the author makes a lines record of
 * them (Decision 2 of docs/plans/entry-lines-and-layout.md).
 */
export const AUTHOR_TYPES = [
  'text',
  'long_text',
  'number',
  'money',
  'quantity',
  'date',
  'datetime',
  'boolean',
  'select',
  'multi_select',
  'reference',
  'attachment',
  'phone',
  'location',
  'formula',
  'lines',
] as const satisfies readonly ValueTypeId[];

export type AuthorType = (typeof AUTHOR_TYPES)[number];

/**
 * Which way the money moves in a record.
 *
 *   in     every entry adds to the total (sales, fees collected)
 *   out    every entry takes from it (expenses, purchases)
 *   both   a field on the row says which way (a "gave / got" choice)
 *   count  the total is a count or a quantity, with no direction
 *   none   nothing is totalled; a directory always says this
 */
export const SETUP_DIRECTIONS = ['in', 'out', 'both', 'count', 'none'] as const;
export type SetupDirection = (typeof SETUP_DIRECTIONS)[number];

/** The aggregates a tile may ask for: the five the device parses out of a summary tile's expr. */
export const SETUP_AGGS = ['sum', 'count', 'avg', 'min', 'max'] as const;
export type SetupAgg = (typeof SETUP_AGGS)[number];

export const SETUP_TILE_SCOPES = ['day', 'week', 'month', 'year', 'all'] as const;
export const SETUP_TILE_OPS = ['is', 'is_not', 'gt', 'gte', 'lt', 'lte', 'set', 'unset'] as const;

/**
 * The patterns a setup may draft: those whose `LINK_PATTERN_ENGINE` is true
 * (only a link that posts a row today). Kept literal so the draft's type stays
 * narrow; links.test.ts holds it to the engine table.
 */
export const SETUP_LINK_PATTERNS = ['feed'] as const satisfies readonly LinkPattern[];
export const SETUP_LINK_OPS = [
  'is',
  'is_not',
  'filled',
  'empty',
  'at_least',
  'at_most',
  'exactly',
  'contains',
  'not_contains',
] as const satisfies readonly LinkConditionOp[];

/**
 * The caps a setup draft holds per part, read by the setup tool's schema
 * (apps/server/src/agent/tools/setup-schema.ts) and stated by the capability
 * manifest. The ceilings of a whole setup are `COMPOSE_LIMITS`.
 */
export const SETUP_DRAFT_LIMITS = {
  /** The fewest choices a select holds. */
  optionsMin: 2,
  /** The most choices a select holds. */
  options: 24,
  /** The longest help line under a field, or a section's title, in characters. */
  help: 80,
  /** The most cards one record pins. */
  tilesPerRecord: 8,
  /** The most conditions one card asks of the rows it counts. */
  tileWhere: 4,
  /** The most groups a ranked card lists. */
  tileGroups: 12,
  /** The most days a trend card looks back. */
  tileDays: 365,
  /** The most rows a list starts with. */
  seedRows: 24,
  /** The most values one starting row names. */
  seedValues: 8,
  /** The most conditions one link asks before it posts. */
  linkWhen: 4,
  /** The most fields one link fills on the row it posts. */
  linkCarry: 12,
  /** The most sections on one form. */
  sections: 8,
  /** The most sections inside one section. */
  innerSections: 6,
  /** The most fields one section holds. */
  sectionFields: 20,
  /** The most fields a sheet asks once at the top. */
  sheetSharedFields: 4,
} as const;

export const SETUP_FORMULA_RESULTS = ['number', 'money', 'quantity', 'text', 'boolean'] as const satisfies readonly FormulaResultType[];
export const SETUP_FORMULA_CALC_OPS = ['add', 'subtract', 'multiply', 'divide'] as const satisfies readonly FormulaCalcOp[];
export const SETUP_FORMULA_TEST_OPS = ['eq', 'ne', 'gt', 'gte', 'lt', 'lte', 'filled', 'empty'] as const satisfies readonly FormulaTestOp[];
export const SETUP_FORMULA_AGG_OPS = ['sum', 'count', 'min', 'max'] as const satisfies readonly FormulaAggOp[];

/**
 * A formula tree that names fields by their draft KEY. The same closed tree as
 * `FormulaNode`, with `fieldKey` where a stored tree holds `fieldId`. A `hop`
 * reads one field of the directory a reference field points at.
 */
export type SetupDraftFormulaNode =
  | { kind: 'field'; fieldKey: string }
  | { kind: 'hop'; via: string; fieldKey: string }
  | { kind: 'const'; value: number | string | boolean }
  | { kind: 'calc'; op: FormulaCalcOp; left: SetupDraftFormulaNode; right: SetupDraftFormulaNode }
  | { kind: 'round'; of: SetupDraftFormulaNode; decimals: number }
  | {
      kind: 'if';
      left: SetupDraftFormulaNode;
      op: FormulaTestOp;
      right?: SetupDraftFormulaNode;
      then: SetupDraftFormulaNode;
      otherwise: SetupDraftFormulaNode;
    }
  | { kind: 'days_between'; from: SetupDraftFormulaNode; to: SetupDraftFormulaNode }
  | { kind: 'concat'; parts: SetupDraftFormulaNode[] }
  /** The first choice that is filled: "the litres, or else the customer's usual". */
  | { kind: 'coalesce'; of: SetupDraftFormulaNode[] }
  /**
   * A total over the rows a multi_select holds, or over the lines a `lines`
   * field holds. `lines` and `of` are field keys: `lines` on this row, `of` on
   * the list or on each line.
   */
  | { kind: 'agg'; op: FormulaAggOp; lines: string; of?: string };

/** A dependent choice, by draft keys: `field` on the target list, `equals` on this record. */
export type SetupDraftChoiceFilter = { field: string; equals: string };

export type SetupDraftFormula = {
  result: FormulaResultType;
  expr: SetupDraftFormulaNode;
  onMissing: FormulaOnMissing;
};

/**
 * The lines of one entry, as a `lines` field carries them: the fields each
 * line holds, and the person's words for one line and for several.
 *
 * A line field may be a `lines` field once more (the guests of each room of a
 * stay), never twice: `LINES_MAX_DEPTH`. A line formula reads up the tree
 * through two keys every line has without being given them: `parent`, the
 * row the line hangs from, and `root`, the entry at the top. So no line field
 * may take either key.
 */
export type SetupDraftLines = {
  /** The person's word for one line: "item", "room", "guest". */
  noun: string;
  /** The same word for several: "items", "rooms", "guests". */
  nouns: string;
  /** The fields of each line, 1 to `LINES_MAX_FIELDS`. */
  fields: SetupDraftField[];
  /** The key of a number field on the same row whose value starts that many lines. */
  countFrom?: string;
  /** The most lines one row holds, 1 to `ENTRY_BATCH_MAX`. Absent is the most. */
  max?: number;
};

/**
 * One section of a record's form, by field keys. A section may hold sections
 * of its own, one level down and no further (`LAYOUT_SECTION_DEPTH_MAX`), and
 * a field the sections leave out goes at the end of the form.
 */
export type SetupDraftSection = {
  title?: string;
  style: LayoutSectionStyle;
  fieldKeys: string[];
  sections?: Array<Omit<SetupDraftSection, 'sections'>>;
};

/**
 * One choice of a select. `tone` is the colour it wears once it is chosen, and
 * it earns its place on a register: Present green and Absent red are read
 * across forty lines faster than either word is. Absent on nearly every choice,
 * and then the choice keeps the brand tint every chip has always drawn.
 */
export type SetupDraftOption = { key: string; label: string; sign?: 1 | -1; tone?: RecordCaptureTone };

export type SetupDraftField = {
  /** snake_case, unique in the record. Bindings, tiles, formulas and links name it. */
  key: string;
  /** The person's word. */
  label: string;
  type: AuthorType;
  required?: boolean;
  sticky?: boolean;
  help?: string;
  /** `select` only: two or more choices. */
  options?: SetupDraftOption[];
  /** `reference` and `multi_select` only: the key of a directory in this draft. */
  targetRecordKey?: string;
  /**
   * `reference` and `multi_select` only: offer only the rows of the target
   * list whose `field` (a reference on THAT list, by its key) holds the row
   * chosen in `equals` (a reference or multi_select on THIS record, by its
   * key). Sub-category filtered by Category: `{ field: 'category', equals:
   * 'category' }`. Apply rewrites each key into the id it mints, on its own
   * record.
   */
  filter?: SetupDraftChoiceFilter;
  /** `multi_select` only: the most rows one entry may hold, 1 to 20. */
  maxChosen?: number;
  /**
   * How the field looks on the form, by shape id from `field-shapes.ts`, with
   * the units or quick amounts the person named. Absent takes the composed
   * default for the type (`composedShapeFor`). A shape the type cannot take is
   * refused as SHAPE_UNKNOWN, so a draft never writes a control the phone
   * cannot draw.
   */
  shape?: ShapeAsk['shape'];
  units?: string[];
  quickAmounts?: string[];
  /** `formula` only. */
  formula?: SetupDraftFormula;
  /**
   * What the field STARTS with when a new entry opens, keyed by field keys the
   * way `formula` is. The same tree, and a different owner of the answer: a
   * formula is worked out for ever, a default is handed over and the person may
   * type straight across it.
   *
   * Never on a `formula` field, on a date, on a set, a file or a place: the
   * validator refuses those through `defaultProblems`, which is the one place
   * the rule lives.
   */
  startsWith?: SetupDraftFormula;
  /**
   * When the field shows on the form: a recipe whose result is boolean, read
   * from typed fields on this row and one hop. A hidden field saves nothing and
   * is never required. Keyed by field keys the way `formula` is.
   */
  showWhen?: SetupDraftFormula;
  /** `lines` only: the fields of each line and the words for one. */
  lines?: SetupDraftLines;
};

export type SetupDraftTileCondition = {
  fieldKey: string;
  op: (typeof SETUP_TILE_OPS)[number];
  /** For `is` and `is_not` on a select: the option key. */
  optionKey?: string;
  /** For the four comparisons on a number, and for `is` on a boolean (1 yes, 0 no). */
  num?: number;
};

export type SetupDraftTile = {
  label: string;
  card: InsightCardId;
  agg: SetupAgg;
  /** Defaults to the record's measure. */
  fieldKey?: string;
  scope?: (typeof SETUP_TILE_SCOPES)[number];
  /** A breakdown needs one. */
  groupByFieldKey?: string;
  days?: number;
  limit?: number;
  where?: SetupDraftTileCondition[];
};

/** One directory row to seed, as pairs. A map would not survive the model's schema. */
export type SetupDraftSeedRow = { values: Array<{ fieldKey: string; value: string | number }> };

export type SetupDraftRecord = {
  key: string;
  /** The person's word for the record. */
  name: string;
  /** The person's word for one entry: "visit", "sale", "bill". */
  entryNoun: string;
  kind: VisibleRecordKind;
  direction: SetupDirection;
  fields: SetupDraftField[];
  /** The field that names a row. */
  headlineFieldKey: string;
  /** The date a record is grouped by. Defaults to the first date field. */
  dateFieldKey?: string;
  /** The number the record totals. Defaults to the first money field. */
  measureFieldKey?: string;
  /** Direction `both` only: the select whose options carry sign 1 or -1. */
  signFieldKey?: string;
  /** The field the form opens on. Defaults to the measure. */
  focusFieldKey?: string;
  /**
   * What this record HOLDS, in one word, which is what its glyph draws: a list
   * of staff is `people`, a list of payment modes is `payments`, a record of
   * expenses is `money`. One of `RECORD_ICON_IDS` and nothing else — a word
   * off the list is refused by the validator (`ICON_UNKNOWN`), never guessed
   * at, because the device draws one icon per name and holds none for a word
   * it has not been told about.
   *
   * Absent leaves the record on its default glyph, which is the grey list a
   * directory wears today and the direction arrow a record wears.
   */
  icon?: RecordIconId;
  /**
   * How this record's entries are WRITTEN, when a form one at a time is not
   * the shape of the job: a record marked for everyone in a list on one day —
   * attendance, a delivery round, a machine check per shift — is written from
   * a SHEET, one line per row of the list and one tap per line.
   *
   * Keyed by draft keys, like everything else here: `overFieldKey` is the
   * reference field whose list the sheet walks, `cellFieldKey` the select the
   * mark is tapped on, `sharedFieldKeys` the fields drawn once at the top
   * (which must hold the day, and defaults to the record's date field when it
   * is left out), and `quickOptionKey` the choice a one-tap shortcut fills
   * down the whole sheet.
   *
   * Absent on nearly every record. A record with no capture is written a form
   * at a time, which is every record in every shipped template but one.
   */
  capture?: {
    mode: 'sheet';
    overFieldKey: string;
    cellFieldKey: string;
    sharedFieldKeys?: string[];
    quickOptionKey?: string;
  };
  /**
   * Where each field shows on the form, by sections. Absent draws the form in
   * field order, which is what nearly every record wants.
   */
  layout?: SetupDraftSection[];
  /** Directories only. */
  seedRows?: SetupDraftSeedRow[];
  /** Records only. Two default tiles are pinned when none are given and there is a measure. */
  tiles?: SetupDraftTile[];
};

export type SetupDraftCarry = {
  toFieldKey: string;
  /** A field of the from record, or a fixed value. One of the two. */
  fromFieldKey?: string;
  const?: string | number | boolean;
};

export type SetupDraftLink = {
  fromRecordKey: string;
  toRecordKey: string;
  pattern: (typeof SETUP_LINK_PATTERNS)[number];
  sign: 1 | -1;
  /** Every condition must hold. On a select, `is` takes an option key. */
  when: Array<{ fieldKey: string; op: LinkConditionOp; value?: string | number | boolean }>;
  carry: SetupDraftCarry[];
};

export type SetupDraft = {
  businessName: string;
  /** ISO 4217. */
  currency: string;
  records: SetupDraftRecord[];
  links?: SetupDraftLink[];
};

/* -------------------------------------------------------------- the keys */

/**
 * One part of a word key, from a draft key. Lower-cased and narrowed to what
 * `WORD_KEY` accepts, so a draft key that is not snake_case is refused by the
 * validator, which names it (RECORD_KEY_INVALID, FIELD_KEY_INVALID), and not
 * by the wire reader, which would only name the word key minted from it.
 */
const part = (key: string): string => key.toLowerCase().replace(/[^a-z0-9_.-]/g, '_');

/** The longest word key `WORD_KEY` reads. */
const WORD_KEY_MAX = 80;

/** FNV-1a over the key, in base 36, eight characters: a short name for a long key. */
const keyHash = (key: string): string => {
  let hash = 0x811c9dc5;
  for (let at = 0; at < key.length; at += 1) {
    hash ^= key.charCodeAt(at);
    hash = Math.imul(hash, 0x01000193) >>> 0;
  }
  return hash.toString(36).padStart(8, '0');
};

/**
 * A word key that fits the reader's 80 characters. Draft keys run to forty
 * characters each, and a field of a line of a line names three of them
 * (`c.stays__rooms__guests.kind.adult`), which the reader would refuse and
 * the whole setup with it. A key past the cap keeps its head and ends in a
 * hash of the whole, so it stays readable in a log and two long keys never
 * meet. Idempotent: a key that fits is itself.
 */
export const fitWordKey = (key: string): string =>
  key.length <= WORD_KEY_MAX ? key : `${key.slice(0, WORD_KEY_MAX - 9)}.${keyHash(key)}`;

/** Where the person's word for one entry of a record lives in `words`. */
export const entryNounWordKey = (recordKey: string): string => fitWordKey(`c.${part(recordKey)}.entry_noun`);

/** Where a lines record's word for several lines lives: "rooms" beside "room". */
export const entryNounsWordKey = (recordKey: string): string => fitWordKey(`c.${part(recordKey)}.entry_nouns`);

/** Unfitted, because an option and a help line are built on it: `say` fits the whole. */
const recordWordKey = (recordKey: string): string => `c.${part(recordKey)}`;
const fieldWordKey = (recordKey: string, fieldKey: string): string => `c.${part(recordKey)}.${part(fieldKey)}`;

const SETUP_LABEL_KEY = 'c.setup';
const SETUP_BODY_KEY = 'c.setup.body';

/** The reader caps a word at 80 characters. */
const WORD_MAX = 80;

/* ------------------------------------------------------------- the slots */

type Letter = EntrySlotLetter;

const LETTER_FOR_HOME: Readonly<Record<string, Letter>> = {
  numeric_slot: 'n',
  text_slot: 't',
  time_slot: 'd',
  reference_slot: 'r',
  boolean_slot: 'b',
};

/** The families that spill into attrs when full; `ENTRY_SLOT_SPILLS` says why a number never does. */
const SPILLS: ReadonlySet<Letter> = new Set(ENTRY_SLOT_SPILLS);

type Placement =
  | { storage: 'slot'; slot: string }
  | { storage: 'attr'; attrKey: string }
  /** A multi_select: its rows live in data.entry_link, so it takes no slot. */
  | { storage: 'link' }
  /** A lines field: its lines are entries of their own record, so the row stores nothing. */
  | { storage: 'children' }
  | { storage: 'full'; message: string };

/** Hands out one record's slots in field order, and attrs keys past the end. */
class Allocator {
  private readonly used: Record<Letter, number>;
  private attrs = 0;

  /** `taken` is what the record already holds: a lines record's parent and root sit on r1 and r2. */
  constructor(taken: Partial<Record<Letter, number>> = {}) {
    this.used = { n: 0, t: 0, d: 0, r: 0, b: 0, ...taken };
  }

  attr(): Placement {
    this.attrs += 1;
    return { storage: 'attr', attrKey: `a${this.attrs}` };
  }

  slot(letter: Letter, type: AuthorType): Placement {
    if (this.used[letter] < ENTRY_SLOT_COUNT[letter]) {
      this.used[letter] += 1;
      return { storage: 'slot', slot: `${letter}${this.used[letter]}` };
    }
    if (SPILLS.has(letter)) return this.attr();
    const message =
      type === 'formula'
        ? `This record has no typed place left to keep a worked-out ${letter === 'n' ? 'number' : letter === 't' ? 'text' : 'yes-or-no'}. It holds ${ENTRY_SLOT_COUNT[letter]} of that kind at most; remove one, or move it to another record.`
        : type === 'reference' || type === 'select'
          ? `This record already holds ${ENTRY_SLOT_COUNT.r} fields that read a list or a set of choices, which is all it can hold. Remove one, or move it to another record.`
          : `This record already holds ${ENTRY_SLOT_COUNT.n} number fields (money, number, quantity or a worked-out number), which is all a total can read. Remove one, or move it to another record.`;
    return { storage: 'full', message };
  }
}

/* ------------------------------------------------------------- the shape */

/**
 * The component and props a composed field of one type gets when nobody asked
 * for a shape: the look a good template ships, not the bare primitive. The
 * agent's later field adds take the same default, so a field the assistant
 * adds in month two looks like the ones it set up on day one.
 */
export const composedShapeFor = (type: ValueTypeId): { component: ComponentId; props: Record<string, unknown> } => {
  switch (type) {
    case 'quantity':
      return { component: 'number.stepper', props: { min: 0, step: 1 } };
    case 'money':
      return { component: 'money.calculator', props: { allow_expression: true } };
    case 'date':
      return { component: 'date.picker', props: { mode: 'date', default: 'today' } };
    case 'datetime':
      return { component: 'date.picker', props: { mode: 'datetime', default: 'now' } };
    case 'reference':
      return { component: 'reference.search_picker', props: { recent_first: true, allow_create: true } };
    case 'phone':
      return { component: 'text.phone', props: { tap_to_call: true } };
    default:
      return { component: PRIMITIVE_FOR[type], props: {} };
  }
};

/* ----------------------------------------------------- the wire formula */

/**
 * The formula as the setup tool's schema carries it: a FLAT LIST of nodes
 * that name their inputs by id, plus the id of the node holding the answer.
 *
 * The nested tree is the natural shape and the wrong one for the wire. The
 * AI SDK renders a recursive zod schema as a draft-7 `$ref` loop; Google's
 * converter cannot inline a loop, falls back to sending the raw schema, and
 * the API then refuses a loop through a required property — or, when the
 * loop points were made optional, chokes flattening it ("Schema is too
 * complex"). Both were observed from this repo within the hour. A flat list
 * keeps the wire schema finite and deterministic, and `readDraftFormulas`
 * rebuilds the tree, proving ids, references, acyclicity and depth as it
 * goes.
 */
export type FlatFormulaNode =
  | { id: string; kind: 'field'; fieldKey: string }
  | { id: string; kind: 'hop'; via: string; fieldKey: string }
  | { id: string; kind: 'const'; value: number | string | boolean }
  | { id: string; kind: 'calc'; op: FormulaCalcOp; left: string; right: string }
  | { id: string; kind: 'round'; of: string; decimals: number }
  | { id: string; kind: 'if'; left: string; op: FormulaTestOp; right?: string; then: string; otherwise: string }
  | { id: string; kind: 'days_between'; from: string; to: string }
  | { id: string; kind: 'concat'; parts: string[] }
  | { id: string; kind: 'coalesce'; of: string[] }
  | { id: string; kind: 'agg'; op: FormulaAggOp; lines: string; of?: string };

export type FlatFormula = {
  result: FormulaResultType;
  onMissing: FormulaOnMissing;
  /** The id of the node that holds the answer. */
  root: string;
  nodes: FlatFormulaNode[];
};

/** The draft as the tool's wire schema carries it: the strict draft, with flat formulas. */
export type SetupDraftFieldWire = Omit<SetupDraftField, 'formula' | 'startsWith' | 'showWhen' | 'lines'> & {
  formula?: FlatFormula;
  /** What the field starts with, flat for the same reason the recipe is. */
  startsWith?: FlatFormula;
  /** When the field shows, flat too. */
  showWhen?: FlatFormula;
  /** The fields of each line, each one flat the same way. */
  lines?: Omit<SetupDraftLines, 'fields'> & { fields: SetupDraftFieldWire[] };
};
export type SetupDraftRecordWire = Omit<SetupDraftRecord, 'fields'> & { fields: SetupDraftFieldWire[] };
export type SetupDraftWire = Omit<SetupDraft, 'records'> & { records: SetupDraftRecordWire[] };

/**
 * One flat formula read back into its tree.
 *
 * The walk itself is `readFormulaWire` in formula-wire.ts, and this is a thin
 * translation either side of it: a draft node names a field by its KEY because
 * no field has an id yet, and the shared reader names it `fieldId`, which is a
 * string it never looks up. So the wire goes in with the keys sitting in the
 * id slot and the tree comes back with them still there, and one reader proves
 * ids, references, acyclicity, reach and both caps for every home a formula
 * has. `coalesce` arrived for nothing more than adding it to the two types.
 *
 * Two seams the translation keeps. The shared reader reports both size caps
 * under FORMULA_TOO_BIG; this file has always called the depth one
 * FORMULA_DEEP and a repair pass branches on the code, so the two are split
 * apart again below. And a problem here carries the formula's own path rather
 * than a path per child, which is what the setup validator's other problems
 * carry too.
 */
export const readDraftFormula = (
  flat: FlatFormula,
  path: string,
): { ok: true; value: SetupDraftFormula } | { ok: false; problems: TemplateProblem[] } => {
  const wire: FormulaWire = {
    root: flat.root,
    nodes: flat.nodes.map((node) =>
      node.kind === 'field'
        ? { id: node.id, kind: 'field', fieldId: node.fieldKey }
        : node.kind === 'hop'
          ? { id: node.id, kind: 'hop', via: node.via, fieldId: node.fieldKey }
          : node,
    ),
  };
  const read = readFormulaWire(wire);
  if (!read.ok) {
    return {
      ok: false,
      problems: read.problems.map((problem) => ({
        code: problem.code === 'FORMULA_TOO_BIG' && problem.message.includes('nests') ? 'FORMULA_DEEP' : problem.code,
        message: problem.message,
        path,
      })),
    };
  }
  return { ok: true, value: { result: flat.result, onMissing: flat.onMissing, expr: keyed(read.expr) } };
};

/** The shared tree back in draft words: `fieldId` was holding a field KEY. */
const keyed = (node: FormulaNode): SetupDraftFormulaNode => {
  switch (node.kind) {
    case 'field':
      return { kind: 'field', fieldKey: node.fieldId };
    case 'hop':
      return { kind: 'hop', via: node.via, fieldKey: node.fieldId };
    case 'const':
      return { kind: 'const', value: node.value };
    case 'calc':
      return { kind: 'calc', op: node.op, left: keyed(node.left), right: keyed(node.right) };
    case 'round':
      return { kind: 'round', of: keyed(node.of), decimals: node.decimals };
    case 'if':
      return {
        kind: 'if',
        left: keyed(node.left),
        op: node.op,
        ...(node.right === undefined ? {} : { right: keyed(node.right) }),
        then: keyed(node.then),
        otherwise: keyed(node.otherwise),
      };
    case 'days_between':
      return { kind: 'days_between', from: keyed(node.from), to: keyed(node.to) };
    case 'concat':
      return { kind: 'concat', parts: node.parts.map(keyed) };
    case 'coalesce':
      return { kind: 'coalesce', of: node.of.map(keyed) };
    case 'agg':
      return { kind: 'agg', op: node.op, lines: node.lines, ...(node.of === undefined ? {} : { of: node.of }) };
  }
};

/**
 * The whole wire draft read into the strict one: every formula rebuilt, or
 * every problem with every one of them at once. The rest of the draft passes
 * through untouched.
 */
export const readDraftFormulas = (
  wire: SetupDraftWire,
): { ok: true; draft: SetupDraft } | { ok: false; problems: TemplateProblem[] } => {
  const problems: TemplateProblem[] = [];
  // Every tree is read the same way, and every one is read even when an
  // earlier one failed: a repair pass should see every problem at once.
  const records = wire.records.map((record) => ({
    ...record,
    fields: record.fields.map((field) => {
      const read = readDraftField(field, `records[${record.key}]`);
      problems.push(...read.problems);
      return read.field;
    }),
  }));
  if (problems.length > 0) return { ok: false, problems };
  return { ok: true, draft: { ...wire, records } };
};

/**
 * One wire field read into the strict one: its formula, starting value and
 * show rule rebuilt from their flat lists, and every line field the same way,
 * with every problem of every tree at once. `at` is the path of the list the
 * field sits in (`records[sales]`); a problem carries
 * `<at>.fields[<key>].<formula|startsWith|showWhen>`, and a line field's
 * `<at>.fields[<key>].lines.fields[<line key>]…`.
 *
 * When there are problems, `field` is the field without the trees that did
 * not read: a discard, since the caller refuses the whole.
 */
export const readDraftField = (
  field: SetupDraftFieldWire,
  at: string,
): { field: SetupDraftField; problems: TemplateProblem[] } => {
  const problems: TemplateProblem[] = [];
  const { formula, startsWith, showWhen, lines, ...rest } = field;
  const path = `${at}.fields[${field.key}]`;
  let held: SetupDraftField = rest;
  for (const [name, flat] of [
    ['formula', formula],
    ['startsWith', startsWith],
    ['showWhen', showWhen],
  ] as const) {
    if (flat === undefined) continue;
    const read = readDraftFormula(flat, `${path}.${name}`);
    if (read.ok) held = { ...held, [name]: read.value };
    else problems.push(...read.problems);
  }
  if (lines !== undefined) {
    held = {
      ...held,
      lines: {
        ...lines,
        fields: lines.fields.map((line) => {
          const read = readDraftField(line, `${path}.lines`);
          problems.push(...read.problems);
          return read.field;
        }),
      },
    };
  }
  return { field: held, problems };
};

/**
 * A tree as the flat list, for the scripted model and the round-trip test.
 * Children land before their parents, so the list reads bottom-up; nothing
 * depends on the order.
 */
export const flattenDraftFormula = (formula: SetupDraftFormula): FlatFormula => {
  const nodes: FlatFormulaNode[] = [];
  let counter = 0;
  const add = (node: SetupDraftFormulaNode): string => {
    const id = `n${(counter += 1)}`;
    switch (node.kind) {
      case 'field':
        nodes.push({ id, kind: 'field', fieldKey: node.fieldKey });
        return id;
      case 'hop':
        nodes.push({ id, kind: 'hop', via: node.via, fieldKey: node.fieldKey });
        return id;
      case 'const':
        nodes.push({ id, kind: 'const', value: node.value });
        return id;
      case 'calc': {
        const left = add(node.left);
        const right = add(node.right);
        nodes.push({ id, kind: 'calc', op: node.op, left, right });
        return id;
      }
      case 'round': {
        const of = add(node.of);
        nodes.push({ id, kind: 'round', of, decimals: node.decimals });
        return id;
      }
      case 'if': {
        const left = add(node.left);
        const right = node.right === undefined ? undefined : add(node.right);
        const then = add(node.then);
        const otherwise = add(node.otherwise);
        nodes.push({ id, kind: 'if', left, op: node.op, ...(right === undefined ? {} : { right }), then, otherwise });
        return id;
      }
      case 'days_between': {
        const from = add(node.from);
        const to = add(node.to);
        nodes.push({ id, kind: 'days_between', from, to });
        return id;
      }
      case 'concat': {
        const parts = node.parts.map(add);
        nodes.push({ id, kind: 'concat', parts });
        return id;
      }
      case 'coalesce': {
        const of = node.of.map(add);
        nodes.push({ id, kind: 'coalesce', of });
        return id;
      }
      case 'agg':
        nodes.push({ id, kind: 'agg', op: node.op, lines: node.lines, ...(node.of === undefined ? {} : { of: node.of }) });
        return id;
    }
  };
  const root = add(formula.expr);
  return { result: formula.result, onMissing: formula.onMissing, root, nodes };
};

/** One field as the wire carries it: every tree flat, and every line field too. */
const flattenField = (field: SetupDraftField): SetupDraftFieldWire => {
  const { formula, startsWith, showWhen, lines, ...rest } = field;
  return {
    ...rest,
    ...(formula === undefined ? {} : { formula: flattenDraftFormula(formula) }),
    ...(startsWith === undefined ? {} : { startsWith: flattenDraftFormula(startsWith) }),
    ...(showWhen === undefined ? {} : { showWhen: flattenDraftFormula(showWhen) }),
    ...(lines === undefined ? {} : { lines: { ...lines, fields: lines.fields.map(flattenField) } }),
  };
};

/** The whole draft as the wire carries it: every formula and every starting value flat. */
export const flattenDraft = (draft: SetupDraft): SetupDraftWire => ({
  ...draft,
  records: draft.records.map((record) => ({ ...record, fields: record.fields.map(flattenField) })),
});

/** The draft tree in the shape the validator and the apply read: `fieldId` holding the KEY. */
const toSpecFormula = (node: SetupDraftFormulaNode): FormulaNode => {
  switch (node.kind) {
    case 'field':
      return { kind: 'field', fieldId: node.fieldKey };
    case 'hop':
      return { kind: 'hop', via: node.via, fieldId: node.fieldKey };
    case 'const':
      return { kind: 'const', value: node.value };
    case 'calc':
      return { kind: 'calc', op: node.op, left: toSpecFormula(node.left), right: toSpecFormula(node.right) };
    case 'round':
      return { kind: 'round', of: toSpecFormula(node.of), decimals: node.decimals };
    case 'if':
      return {
        kind: 'if',
        left: toSpecFormula(node.left),
        op: node.op,
        ...(node.right === undefined ? {} : { right: toSpecFormula(node.right) }),
        then: toSpecFormula(node.then),
        otherwise: toSpecFormula(node.otherwise),
      };
    case 'days_between':
      return { kind: 'days_between', from: toSpecFormula(node.from), to: toSpecFormula(node.to) };
    case 'concat':
      return { kind: 'concat', parts: node.parts.map(toSpecFormula) };
    case 'coalesce':
      return { kind: 'coalesce', of: node.of.map(toSpecFormula) };
    case 'agg':
      return { kind: 'agg', op: node.op, lines: node.lines, ...(node.of === undefined ? {} : { of: node.of }) };
  }
};

/**
 * A draft formula as a `FormulaSpec`, with field KEYS in its id slots: what a
 * composed formula field's spec, a composed link's worked-out fill and the
 * studio's cards read.
 */
export const draftFormulaSpec = (formula: SetupDraftFormula): FormulaSpec => ({
  result: formula.result,
  expr: toSpecFormula(formula.expr),
  onMissing: formula.onMissing,
});

/**
 * A link's carry from one field of the entry it fires on, as compose reads
 * it. A typed field is carried as itself. A worked-out field (a Total over
 * a bill's lines) is carried as a worked-out fill that reads it: its answer
 * is kept on the entry before the link fires (entry-create.ts reads the row
 * back with its formulas filled), and a worked-out fill may read a formula
 * field (links.ts `formulaInfo`), where a plain carry of it is refused as a
 * mismatch of kinds. Blank when the entry has no answer, never a zero
 * nobody meant (16 Oct, Gupta Kirana: Total Amount as Amount Owed).
 */
export const carryMappingOf = (source: Pick<SetupDraftField, 'type' | 'formula'> | undefined, fieldKey: string): ComposedMapping =>
  source?.type === 'formula' && source.formula !== undefined
    ? { kind: 'formula', spec: draftFormulaSpec({ result: source.formula.result, expr: { kind: 'field', fieldKey }, onMissing: 'blank' }) }
    : { kind: 'field', fieldKey };

/* ------------------------------------------------------------ the author */

/**
 * The filter and limit of a field that points at a list, as the spec carries
 * them: keyed exactly as the draft keys them, because apply rewrites `equals`
 * on this record and `field` on the target list. Nothing when the draft set
 * neither; the validator judges a pair that is set on the wrong type.
 */
const choiceSpecOf = (field: SetupDraftField): { spec?: FieldSpec } => {
  const spec: { filter?: SetupDraftChoiceFilter; maxChosen?: number } = {
    ...(field.filter === undefined ? {} : { filter: { field: field.filter.field, equals: field.filter.equals } }),
    ...(field.maxChosen === undefined ? {} : { maxChosen: field.maxChosen }),
  };
  return Object.keys(spec).length === 0 ? {} : { spec };
};

type Authored = { setup: ComposedSetup; problems: TemplateProblem[] };

/**
 * The key the lines record of one `lines` field takes: its owner's key and the
 * field's, so two lines records never meet and a person's record key (which a
 * draft key never doubles an underscore in by habit) almost never does.
 */
export const linesRecordKey = (ownerKey: string, fieldKey: string): string => `${ownerKey}__${fieldKey}`;

/** Every field of a list, and every field of every line under it. */
const everyField = (fields: readonly SetupDraftField[]): SetupDraftField[] =>
  fields.flatMap((field) => [field, ...(field.lines === undefined ? [] : everyField(field.lines.fields))]);

/** The sections of a record's form, by field keys, as the layout tree holds them. */
const layoutOf = (sections: readonly SetupDraftSection[]): RecordLayout => {
  const section = (one: Omit<SetupDraftSection, 'sections'>, inner: readonly LayoutNode[]): LayoutNode => ({
    kind: 'section',
    title: one.title === undefined || one.title.trim() === '' ? null : clean(one.title),
    style: one.style,
    children: [...one.fieldKeys.map((fieldId): LayoutNode => ({ kind: 'field', fieldId, span: 'auto' })), ...inner],
  });
  return {
    version: LAYOUT_VERSION,
    nodes: sections.map((one) => section(one, (one.sections ?? []).map((inner) => section(inner, [])))),
  };
};

const clean = (word: string): string => word.trim().slice(0, WORD_MAX);

const isText = (value: unknown): value is string => typeof value === 'string';

/**
 * The spec, the words and the links, from the draft. Total: every draft that
 * is the shape authors to SOME setup, and what authoring could not place is
 * reported beside it rather than thrown. Whether the setup is sound is
 * `composeProblems`' question, asked by `authorProblems`.
 */
const author = (draft: SetupDraft): Authored => {
  const words: Record<string, string> = {};
  const problems: TemplateProblem[] = [];
  const say = (raw: string, word: string): string => {
    const key = fitWordKey(raw);
    words[key] = clean(word);
    return key;
  };

  const businessName = draft.businessName.trim();
  say(SETUP_LABEL_KEY, businessName);
  say(SETUP_BODY_KEY, draft.records.map((record) => record.name.trim()).join(', '));

  /**
   * One record's fields, or one lines record's: the same placement, shape and
   * words for both, and a `lines` field authors its own lines record here, one
   * level down, through the same function.
   *
   * `ordFrom` is where the fields start: 1 on a record, 3 on a lines record,
   * whose parent and root (made by `linesRecordsOf`) are 1 and 2.
   */
  const authorFields = (
    ownerKey: string,
    list: readonly SetupDraftField[],
    at: string,
    slots: Allocator,
    ordFrom: number,
  ): TemplateFieldSpec[] =>
    list.map((field, index): TemplateFieldSpec => {
      const path = `${at}.fields[${field.key}]`;
      // The shape the draft asked for, over the composed default's tuning
      // (allow_expression, recent_first) the way a gallery re-shape carries
      // it; the default itself when the draft said nothing.
      const usual = composedShapeFor(field.type);
      const asked = resolveShape(field.type, field, usual.props);
      if (asked !== null && !asked.ok) problems.push({ code: 'SHAPE_UNKNOWN', message: asked.message, path });
      const { component, props } = asked !== null && asked.ok ? asked : usual;

      const home = VALUE_TYPES[field.type].home;
      const letter: Letter | null =
        field.type === 'formula'
          ? field.formula === undefined
            ? 'n'
            : slotFamilyFor(field.formula.result)
          : (LETTER_FOR_HOME[home] ?? null);
      const placement: Placement =
        home === 'link_table'
          ? { storage: 'link' }
          : home === 'child_record'
            ? { storage: 'children' }
            : letter === null
              ? slots.attr()
              : slots.slot(letter, field.type);
      if (placement.storage === 'full') {
        problems.push({ code: 'SLOTS_FULL', message: placement.message, path });
      }
      // A field in attrs cannot promise a value: attrs holds no not-null.
      const inAttrs = placement.storage === 'attr';

      // A lines field's own record: its words, its line fields, and how many
      // lines it starts with. Keyed by draft keys, as everything here is.
      const lines = field.lines;
      const linesKey = linesRecordKey(ownerKey, field.key);

      return {
        key: field.key,
        labelKey: say(fieldWordKey(ownerKey, field.key), field.label),
        type: field.type,
        tier: 'custom',
        ...(field.required === true && !inAttrs ? { required: true } : {}),
        ...(field.sticky === true ? { sticky: true } : {}),
        component,
        props,
        ...(placement.storage === 'attr'
          ? { storage: 'attr' as const, attrKey: placement.attrKey }
          : placement.storage === 'link'
            ? { storage: 'link' as const }
            : placement.storage === 'children'
              ? { storage: 'children' as const }
              : { storage: 'slot' as const, ...(placement.storage === 'slot' ? { slot: placement.slot } : {}) }),
        ...(field.targetRecordKey === undefined ? {} : { targetRecordKey: field.targetRecordKey }),
        ...(field.options === undefined
          ? {}
          : {
              options: field.options.map((option) => ({
                key: option.key,
                labelKey: say(`${fieldWordKey(ownerKey, field.key)}.${part(option.key)}`, option.label),
                ...(option.sign === undefined ? {} : { sign: option.sign }),
                ...(option.tone === undefined ? {} : { tone: option.tone }),
              })),
            }),
        ...(field.help === undefined || field.help.trim() === ''
          ? {}
          : { helpText: say(`${fieldWordKey(ownerKey, field.key)}.help`, field.help) }),
        ...(field.formula === undefined
          ? choiceSpecOf(field)
          : {
              spec: {
                formula: {
                  result: field.formula.result,
                  expr: toSpecFormula(field.formula.expr),
                  onMissing: field.formula.onMissing,
                },
              },
            }),
        // Keyed exactly as `spec` is, because apply rewrites the two the same
        // way: a field key becomes the id it mints, and a select default's
        // choice key becomes the option id beside it.
        ...(field.startsWith === undefined
          ? {}
          : {
              startsWith: {
                result: field.startsWith.result,
                expr: toSpecFormula(field.startsWith.expr),
                onMissing: field.startsWith.onMissing,
              },
            }),
        ...(field.showWhen === undefined
          ? {}
          : {
              showWhen: {
                result: field.showWhen.result,
                expr: toSpecFormula(field.showWhen.expr),
                onMissing: field.showWhen.onMissing,
              },
            }),
        ...(lines === undefined
          ? {}
          : {
              lines: {
                key: linesKey,
                nameKey: say(recordWordKey(linesKey), field.label),
                nounKeys: {
                  one: say(entryNounWordKey(linesKey), lines.noun),
                  other: say(entryNounsWordKey(linesKey), lines.nouns),
                },
                fields: authorFields(linesKey, lines.fields, `${path}.lines`, new Allocator({ r: 2 }), 3),
                ...(lines.countFrom === undefined ? {} : { countFromKey: lines.countFrom }),
                ...(lines.max === undefined ? {} : { max: lines.max }),
              },
            }),
        ord: ordFrom + index,
      };
    });

  const records: TemplateRecordSpec[] = draft.records.map((record) => {
    const rk = record.key;
    const at = `${COMPOSED_TEMPLATE_ID}.records[${rk}]`;
    const nameKey = say(recordWordKey(rk), record.name);
    say(entryNounWordKey(rk), record.entryNoun);

    const fields = authorFields(rk, record.fields, at, new Allocator(), 1);

    const typeOf = (key: string | undefined): AuthorType | undefined =>
      key === undefined ? undefined : record.fields.find((field) => field.key === key)?.type;
    const firstOf = (types: readonly AuthorType[]): string | undefined =>
      record.fields.find((field) => types.includes(field.type))?.key;

    // The bindings follow the direction. A directory binds a title and nothing
    // else: its rows are picked, not totalled.
    const bindings: TemplateBindingSpec[] = [{ purpose: 'title', fieldKey: record.headlineFieldKey }];
    let measureKey: string | undefined;
    let dateKey: string | undefined;
    if (record.kind === 'record') {
      dateKey = record.dateFieldKey ?? firstOf(['date', 'datetime']);
      if (dateKey !== undefined) bindings.push({ purpose: 'time_axis', fieldKey: dateKey });

      if (record.direction !== 'none') {
        measureKey =
          record.measureFieldKey ??
          firstOf(['money']) ??
          (record.direction === 'count' ? firstOf(['quantity', 'number']) : undefined);
      }
      if (measureKey !== undefined) {
        bindings.push({ purpose: 'measure', fieldKey: measureKey });
        if (record.direction === 'in') bindings.push({ purpose: 'sign', constNum: 1 });
        if (record.direction === 'out') bindings.push({ purpose: 'sign', constNum: -1 });
        if (record.direction === 'both') {
          const signKey =
            record.signFieldKey ??
            record.fields.find(
              (field) =>
                field.type === 'select' &&
                (field.options ?? []).length > 0 &&
                (field.options ?? []).every((option) => option.sign !== undefined),
            )?.key;
          if (signKey === undefined) {
            problems.push({
              code: 'SIGN_FIELD_MISSING',
              message: `"${record.name}" moves money both ways, so it needs signFieldKey: a select whose every option carries sign 1 (in) or -1 (out).`,
              path: at,
            });
          } else {
            bindings.push({ purpose: 'sign', fieldKey: signKey });
          }
        }
      }

      const focusKey = record.focusFieldKey ?? measureKey;
      if (focusKey !== undefined && typeOf(focusKey) !== 'formula') {
        bindings.push({ purpose: 'focused_input', fieldKey: focusKey });
      }
    }

    // Two views for a record, none for a directory: the same pair every
    // shipped template carries, named by the shipped keys.
    const views: TemplateViewSpec[] = [];
    if (record.kind === 'record') {
      views.push({
        nameKey: 'view.common.all_entries',
        type: 'list',
        ord: 1,
        spec: dateKey === undefined ? {} : { group_by: dateKey, sort: [{ fieldKey: dateKey, desc: true }] },
      });

      const tiles: TemplateSummaryTile[] = [];
      if (record.tiles !== undefined && record.tiles.length > 0) {
        for (const [index, tile] of record.tiles.entries()) {
          const of = tile.fieldKey ?? measureKey ?? (tile.agg === 'count' ? record.headlineFieldKey : undefined);
          if (of === undefined) {
            problems.push({
              code: 'TILE_FIELD_MISSING',
              message: `Tile "${tile.label}" of "${record.name}" names no fieldKey and the record has no measure to total.`,
              path: `${at}.views[view.common.summary]`,
            });
            continue;
          }
          tiles.push({
            label_key: say(`${recordWordKey(rk)}.tile.${index + 1}`, tile.label),
            expr: `${tile.agg}:${of}`,
            card: tile.card,
            ...(index === 0 ? { style: 'hero' as const } : {}),
            ...(tile.scope === undefined ? {} : { scope: tile.scope }),
            ...(tile.groupByFieldKey === undefined ? {} : { group_by: tile.groupByFieldKey }),
            ...(tile.days === undefined ? {} : { days: tile.days }),
            ...(tile.limit === undefined ? {} : { limit: tile.limit }),
            ...(tile.where === undefined || tile.where.length === 0
              ? {}
              : {
                  where: tile.where.map(
                    (condition): TemplateTileCondition => ({
                      fieldKey: condition.fieldKey,
                      op: condition.op,
                      ...(condition.optionKey === undefined ? {} : { optionKey: condition.optionKey }),
                      ...(condition.num === undefined ? {} : { num: condition.num }),
                    }),
                  ),
                }),
          });
        }
      } else if (measureKey !== undefined) {
        tiles.push(
          { label_key: 'stat.today', expr: `sum:${measureKey}`, scope: 'day', style: 'hero', card: 'insight.stat' },
          { label_key: 'stat.this_month', expr: `sum:${measureKey}`, scope: 'month', card: 'insight.stat' },
        );
      }
      views.push({ nameKey: 'view.common.summary', type: 'summary', ord: 2, spec: { summaries: tiles } });
    }

    const seedRows =
      record.seedRows === undefined || record.seedRows.length === 0
        ? undefined
        : record.seedRows.map((row, rowIndex) =>
            Object.fromEntries(
              row.values.map(({ fieldKey, value }) => [
                fieldKey,
                isText(value) ? say(`${recordWordKey(rk)}.seed.${rowIndex + 1}.${part(fieldKey)}`, value) : value,
              ]),
            ),
          );

    // How the entries are written. The draft names the day only when it wants
    // something else at the top of the sheet; left out, the shared fields are
    // the record's own day and nothing more, which is the sheet the design
    // draws. Every key is checked by `validateTemplateSpec` a moment later —
    // one place owns the three rules, and it is the same one that owns them
    // for a shipped template.
    const capture =
      record.capture === undefined
        ? undefined
        : {
            mode: 'sheet' as const,
            overFieldKey: record.capture.overFieldKey,
            cellFieldKey: record.capture.cellFieldKey,
            sharedFieldKeys: record.capture.sharedFieldKeys ?? (dateKey === undefined ? [] : [dateKey]),
            ...(record.capture.quickOptionKey === undefined ? {} : { quickOptionKey: record.capture.quickOptionKey }),
          };

    return {
      key: rk,
      kind: record.kind,
      nameKey,
      fields,
      bindings,
      views,
      // What the record holds, straight through: it names no field and needs
      // no rewriting, and the one word it is allowed to be is checked by
      // `validateTemplateSpec` beside every other rule (ICON_UNKNOWN).
      ...(record.icon === undefined ? {} : { icon: record.icon }),
      ...(capture === undefined ? {} : { capture }),
      // Where each field shows, by field keys; apply rewrites them to ids and
      // `validateTemplateSpec` reads the tree as every device will.
      ...(record.layout === undefined || record.layout.length === 0 ? {} : { layout: layoutOf(record.layout) }),
      ...(seedRows === undefined ? {} : { seedRows }),
    };
  });

  const links: ComposedLink[] = (draft.links ?? []).map((link, index) => {
    const path = `${COMPOSED_TEMPLATE_ID}.links[${index}]`;
    const fieldMap: ComposedLink['fieldMap'] = {};
    const fromFields = draft.records.find((record) => record.key === link.fromRecordKey)?.fields ?? [];
    for (const carry of link.carry) {
      if (carry.fromFieldKey !== undefined) {
        const source = fromFields.find((field) => field.key === carry.fromFieldKey);
        fieldMap[carry.toFieldKey] = carryMappingOf(source, carry.fromFieldKey);
      } else if (carry.const !== undefined) {
        fieldMap[carry.toFieldKey] = { kind: 'const', value: carry.const };
      } else {
        problems.push({
          code: 'LINK_CARRY_EMPTY',
          message: `The link into "${link.toRecordKey}" carries nothing into "${carry.toFieldKey}": give fromFieldKey or const.`,
          path,
        });
      }
    }
    return {
      fromRecordKey: link.fromRecordKey,
      toRecordKey: link.toRecordKey,
      pattern: link.pattern,
      sign: link.sign,
      mode: 'auto',
      trigger: {
        all: link.when.map((condition) => ({
          any: [
            {
              fieldKey: condition.fieldKey,
              op: condition.op,
              ...(condition.value === undefined ? {} : { value: condition.value }),
            },
          ],
        })),
      },
      fieldMap,
    };
  });

  // An invoice line's amount is money too, so a line can be why a currency is asked.
  const holdsMoney = draft.records.some((record) => everyField(record.fields).some((field) => field.type === 'money'));

  const spec: TemplateSpec = {
    id: COMPOSED_TEMPLATE_ID,
    version: 1,
    labelKey: SETUP_LABEL_KEY,
    descriptionKey: SETUP_BODY_KEY,
    nounKey: 'noun.record',
    entryNounKey: 'noun.entry',
    currencyRequired: holdsMoney,
    records,
    sampleEntries: [],
  };

  return {
    setup: {
      businessName,
      currency: draft.currency.trim().toUpperCase(),
      spec,
      words,
      links,
    },
    problems,
  };
};

/** The `ComposedSetup` a draft authors to. Pure; never throws on a draft that is the shape. */
export const authorSetup = (draft: SetupDraft): ComposedSetup => author(draft).setup;

/**
 * The setup a draft authors to, with what authoring itself could not place
 * (SLOTS_FULL, SHAPE_UNKNOWN, SIGN_FIELD_MISSING, …) beside it: the studio's
 * judge reads both, and asks the composed checks itself.
 */
export const authorSetupWithProblems = (draft: SetupDraft): { setup: ComposedSetup; problems: TemplateProblem[] } => author(draft);

/**
 * Every problem with what a draft authors to, at once: what authoring could
 * not place, then what the wire reader refuses, then what the composed
 * validator refuses. Empty means `authorSetup(draft)` may be posted to
 * POST /v1/onboarding/compose.
 */
export const authorProblems = (draft: SetupDraft): TemplateProblem[] => {
  const { setup, problems } = author(draft);
  const read = readComposedSetup(setup);
  if (!read.ok) {
    return [...problems, { code: read.refusal.code, message: read.refusal.message, path: COMPOSED_TEMPLATE_ID }];
  }
  return [...problems, ...composeProblems(read.value)];
};
